import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { BarChart3 } from 'lucide-react';

// ── Reports (2026-08-15, "fixing not postponing" ruling) ────────────────────
// A real, queryable operations report over the same live tables the rest of
// the app uses — no mock numbers. Windowed counts from cr.or_cases (public
// view) plus evidence review backlog. Grows with the documentation-tracking
// table when that lands; every figure below is computed from live rows.

interface CaseRow {
    case_id: number;
    case_status: string | null;
    schedule_status: string | null;
    stage: string | null;
    scheduled_start: string | null;
    actual_start: string | null;
    estimated_duration_minutes: number | null;
    or_room: string | null;
    surgeon_name: string | null;
}

const WINDOWS = [
    { key: '7', label: 'Last 7 days', days: 7 },
    { key: '30', label: 'Last 30 days', days: 30 },
] as const;

function isoDaysAgo(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toLocaleDateString('en-CA'); // LOCAL calendar day
}

export function ReportsRoute() {
    const [win, setWin] = useState<(typeof WINDOWS)[number]>(WINDOWS[0]);
    const from = isoDaysAgo(win.days);
    const today = new Date().toLocaleDateString('en-CA');

    const { data, isLoading, error } = useQuery({
        queryKey: ['ops-report', win.key, today],
        queryFn: async () => {
            const [casesRes, evidenceRes] = await Promise.all([
                supabase
                    .from('or_cases')
                    .select('case_id, case_status, schedule_status, stage, scheduled_start, actual_start, estimated_duration_minutes, or_room, surgeon_name')
                    .gte('case_date', from)
                    .lte('case_date', today)
                    .is('deleted_at', null),
                supabase
                    .schema('cr')
                    .from('case_evidence_assets')
                    .select('id', { count: 'exact', head: true })
                    .eq('review_status', 'pending'),
            ]);
            if (casesRes.error) throw casesRes.error;
            return {
                cases: (casesRes.data ?? []) as CaseRow[],
                pendingEvidence: evidenceRes.count ?? 0,
            };
        },
    });

    const cases = data?.cases ?? [];
    const completed = cases.filter((c) => c.case_status === 'completed' || c.schedule_status === 'completed');
    const cancelled = cases.filter((c) => c.case_status === 'cancelled' || c.schedule_status === 'cancelled');
    const started = cases.filter((c) => c.actual_start != null && c.scheduled_start != null);
    const onTime = started.filter(
        (c) => new Date(c.actual_start as string).getTime() - new Date(c.scheduled_start as string).getTime() <= 15 * 60_000
    );
    const byRoom = new Map<string, number>();
    for (const c of cases) byRoom.set(c.or_room ?? '—', (byRoom.get(c.or_room ?? '—') ?? 0) + 1);
    const bySurgeon = new Map<string, number>();
    for (const c of cases) bySurgeon.set(c.surgeon_name ?? '—', (bySurgeon.get(c.surgeon_name ?? '—') ?? 0) + 1);

    return (
        <div className="min-h-full bg-pt-darker">
            <header className="border-b border-pt-border/60 px-6 py-4 flex items-center gap-3">
                <span className="text-pt-cyan"><BarChart3 className="w-5 h-5" /></span>
                <div className="flex-1">
                    <h1 className="text-lg font-display font-semibold text-pt-text">Reports</h1>
                    <p className="text-xs text-pt-text-muted">Facility operations — computed live from case and evidence records</p>
                </div>
                <div className="flex gap-1">
                    {WINDOWS.map((w) => (
                        <button
                            key={w.key}
                            type="button"
                            onClick={() => setWin(w)}
                            className={[
                                'text-xs px-3 py-1.5 rounded border transition-colors',
                                win.key === w.key
                                    ? 'border-pt-cyan/60 text-pt-cyan bg-pt-cyan/10'
                                    : 'border-pt-input-border text-pt-text-muted hover:text-pt-text',
                            ].join(' ')}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>
            </header>

            <div className="p-6 space-y-6">
                {isLoading && <div className="text-pt-text-muted text-sm">Computing…</div>}
                {error != null && (
                    <div className="text-sm text-red-400">{error instanceof Error ? error.message : 'Failed to load report'}</div>
                )}
                {!isLoading && error == null && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            {[
                                { label: 'Cases', value: cases.length },
                                { label: 'Completed', value: completed.length },
                                { label: 'Cancelled', value: cancelled.length },
                                { label: 'On-time starts', value: started.length ? `${onTime.length}/${started.length}` : '—' },
                                { label: 'Evidence pending review', value: data?.pendingEvidence ?? 0 },
                            ].map((kpi) => (
                                <div key={kpi.label} className="border border-pt-border rounded-lg bg-pt-panel px-4 py-3">
                                    <div className="text-2xl font-semibold text-pt-cyan">{kpi.value}</div>
                                    <div className="text-xs text-pt-text-muted mt-1">{kpi.label}</div>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[
                                { title: 'Cases by room', map: byRoom },
                                { title: 'Cases by surgeon', map: bySurgeon },
                            ].map((tbl) => (
                                <div key={tbl.title} className="border border-pt-border rounded-lg bg-pt-panel">
                                    <div className="px-4 py-2.5 border-b border-pt-border/60 text-xs uppercase tracking-wide text-pt-text-muted">{tbl.title}</div>
                                    {tbl.map.size === 0 ? (
                                        <div className="px-4 py-6 text-sm text-pt-text-muted">No cases in this window.</div>
                                    ) : (
                                        <ul className="divide-y divide-pt-border/40">
                                            {[...tbl.map.entries()].sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                                <li key={k} className="px-4 py-2 flex items-center justify-between text-sm">
                                                    <span className="text-pt-text truncate">{k}</span>
                                                    <span className="text-pt-text-muted">{v}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
